import React from "react";
import { Link } from "react-router-dom";
import "./orgIssueProfile.css";

export function formatDate(value) {
    return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// Enacted bills still moving through rulemaking, shared by an organization's Issue Profile and a
// Workspace's Rulemaking tab. `renderBadge(bill)` adds whatever stance or client tags the caller has.
export default function RulemakingBillList({ bills, renderBadge }) {
    return bills.map(bill => (
        <div className="issue-rule" key={bill.bill_id}>
            <div className="issue-bill-line">
                <Link className="issue-bill-id" to={`/legislation/bill/${bill.bill_id}`}>{bill.display}</Link>
                <span className="issue-bill-title" title={bill.title}>{bill.title}</span>
                {renderBadge && renderBadge(bill)}
            </div>
            <div className="issue-rule-meta">
                <span>{bill.chapter || "Chaptered"} · signed {formatDate(bill.chaptered_at)}</span>
                <span>Takes effect ~{formatDate(bill.effective_date)}</span>
                {bill.next_deadline && (
                    <span className={bill.deadline_passed ? "issue-rule-late" : "issue-rule-due"}>
                        {bill.deadline_passed ? "Statutory deadline passed" : "Statutory deadline"} {formatDate(bill.next_deadline)}
                    </span>
                )}
            </div>
            {bill.agencies.length > 0 && (
                <div className="issue-rule-agencies">
                    {bill.agencies.map(a => a.organization_id && a.kind === "agency"
                        ? <Link key={a.key} to={`/organization/${a.organization_id}`}>{a.label}</Link>
                        : <span key={a.key}>{a.label}</span>)}
                </div>
            )}
            {bill.notices.map(n => (
                <div className="issue-notice" key={n.file_number}>
                    <div className="issue-notice-head">
                        <span className="issue-notice-agency">
                            {n.agency_organization_id ? <Link to={`/organization/${n.agency_organization_id}`}>{n.agency}</Link> : n.agency}
                        </span>
                        <span className="issue-notice-subject">{n.subject}</span>
                    </div>
                    <div className="issue-rule-meta">
                        {n.hearing_status === "scheduled" && n.hearing_date && (
                            <span className={n.next_event && new Date(n.hearing_date) >= new Date(n.next_event) ? "issue-rule-due" : ""}>
                                Hearing {formatDate(n.hearing_date)}{n.hearing_time ? `, ${n.hearing_time}` : ""}{n.hearing_location ? ` · ${n.hearing_location}` : ""}
                            </span>
                        )}
                        {n.hearing_status === "none" && <span>No hearing scheduled (one can be requested)</span>}
                        {n.comment_deadline && <span>Comments due {formatDate(n.comment_deadline)}</span>}
                        <span>Noticed {formatDate(n.register_date)}</span>
                        {n.adopted && (
                            <span className="issue-rule-adopted">
                                <i className="bi bi-check-circle-fill"></i> Adopted: filed {formatDate(n.adopted.filed_date)}{n.adopted.effective_date ? `, effective ${formatDate(n.adopted.effective_date)}` : ""}
                            </span>
                        )}
                        {n.source_url && <a href={n.source_url} target="_blank" rel="noopener noreferrer">Notice {n.file_number}</a>}
                    </div>
                    <div className="issue-notice-match">
                        {n.match === "chapter" && "Cites this bill's chapter"}
                        {n.match === "bill" && "Names this bill"}
                        {n.match === "sections" && `Possibly related: cites ${n.shared_sections.join(", ")}`}
                    </div>
                </div>
            ))}
            {bill.upcoming_events?.length > 0 && (
                <div className="issue-notice">
                    <div className="issue-notice-head">
                        <span className="issue-notice-agency">Coming up at the agencies named in this bill</span>
                    </div>
                    {bill.upcoming_events.map(e => (
                        <div className="issue-rule-meta" key={`${e.agency}-${e.type}-${e.date}-${e.title}`}>
                            <span className="issue-rule-due">{formatDate(e.date)}{e.time ? `, ${e.time}` : ""}</span>
                            <span>{e.agency}: {e.type === "hearing" ? "Public hearing" : e.title}{e.type === "hearing" ? ` on ${e.title}` : ""}</span>
                            {e.location && <span>{e.location}</span>}
                            {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer">{e.type === "hearing" ? "Notice" : "Agenda"}</a>}
                        </div>
                    ))}
                    <div className="issue-notice-match">Calendar items for the whole body; not necessarily about this bill</div>
                </div>
            )}
            {bill.adopted_actions?.map(a => (
                <div className="issue-notice" key={a.oal_file_number}>
                    <div className="issue-notice-head">
                        <span className="issue-notice-agency">
                            {a.agency_organization_id ? <Link to={`/organization/${a.agency_organization_id}`}>{a.agency}</Link> : a.agency}
                        </span>
                        <span className="issue-notice-subject">{a.subject}</span>
                    </div>
                    <div className="issue-rule-meta">
                        <span className="issue-rule-adopted">
                            <i className="bi bi-check-circle-fill"></i> Adopted: filed {formatDate(a.filed_date)}{a.effective_date ? `, effective ${formatDate(a.effective_date)}` : ""}
                        </span>
                        {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer">OAL file {a.oal_file_number}</a>}
                    </div>
                    <div className="issue-notice-match">Filing summary names this bill</div>
                </div>
            ))}
            {bill.notice_count > bill.notices.length && (
                <p className="issue-note">{bill.notice_count - bill.notices.length} more possibly related notice(s) not shown</p>
            )}
            {bill.duties.map((d, i) => (
                <p className="issue-rule-duty" key={i}>
                    <span className="issue-rule-kind">{d.kind === "regulations" ? "Regulations" : "Guidance"}</span> {d.sentence}
                </p>
            ))}
        </div>
    ));
}
